/**
 * The portal access token, held PER TAB.
 *
 * It used to live only in localStorage, which every tab shares: signing in as
 * an admin in a second tab silently swapped the token under a buyer tab (and a
 * buyer profile fetch still in flight could land on top of the admin login), so
 * tabs ended up showing one account while acting as another.
 *
 * Now each tab owns its token in sessionStorage. localStorage keeps only the
 * most recent sign-in, used to seed a NEW tab (so opening a link in a new tab
 * stays signed in) — after that the tab is independent of other tabs' logins.
 */
const KEY = "token";

function seedFromShared(): void {
  try {
    if (window.sessionStorage.getItem(KEY) === null) {
      const shared = window.localStorage.getItem(KEY);
      if (shared) window.sessionStorage.setItem(KEY, shared);
    }
  } catch {
    /* storage unavailable - getAuthToken falls back below */
  }
}
seedFromShared();

export function getAuthToken(): string | null {
  try {
    return window.sessionStorage.getItem(KEY);
  } catch {
    return window.localStorage.getItem(KEY);
  }
}

export function setAuthToken(token: string): void {
  try {
    window.sessionStorage.setItem(KEY, token);
  } catch {
    /* ignore */
  }
  window.localStorage.setItem(KEY, token);
}

/** Drop this tab's session. The shared seed is removed only if it is this
 *  same session — signing out of a buyer tab must not sign out the admin who
 *  logged in afterwards in another tab. Removing it fires a `storage` event,
 *  which signs out the other tabs holding this same token (AuthContext). */
export function clearAuthToken(): void {
  const mine = getAuthToken();
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  if (!mine || window.localStorage.getItem(KEY) === mine) {
    window.localStorage.removeItem(KEY);
  }
}

/** localStorage key, for the cross-tab `storage` listener. */
export const AUTH_TOKEN_SHARED_KEY = KEY;
